import { FormEvent, useEffect, useState } from "react";
import { ArrowRight, LogOut, MailCheck, RefreshCw, ShieldCheck } from "lucide-react";

import { apiGetAuthed, apiPost, apiPostAuthed } from "../lib/api";
import { Button, Card, LinkButton, StatusPill } from "./ui";

type WebSession = {
  email: string;
  emailVerified: boolean;
  accessToken: string;
  refreshToken: string;
};

type AccountResponse = {
  user: {
    email: string;
    emailVerified: boolean;
    referralCode: string;
  };
  entitlement: {
    plan: "free" | "premium";
    status: "none" | "trialing" | "active" | "past_due" | "canceled";
    premiumActive: boolean;
    referralDiscountPercent: number;
  };
  billingPlan: "rookie" | "pro_monthly" | "pro_yearly";
};

type AccountState = "loading" | "ready" | "error";
type ActionState = "idle" | "submitting";

function readSession(): WebSession | null {
  try {
    const rawSession = window.localStorage.getItem("ibox-web-session");
    if (!rawSession) return null;
    const session = JSON.parse(rawSession) as Partial<WebSession>;
    if (!session.accessToken || !session.refreshToken || !session.email) return null;
    return session as WebSession;
  } catch {
    return null;
  }
}

function AccountPlanBadge({ billingPlan }: { billingPlan: AccountResponse["billingPlan"] }) {
  const label = billingPlan === "pro_yearly" ? "Pro Annually" : billingPlan === "pro_monthly" ? "Pro Monthly" : "Rookie";
  const dotClassName = billingPlan === "rookie" ? "bg-muted" : "bg-success";

  return (
    <span className="inline-flex w-fit items-center gap-2 self-start rounded-md border border-border bg-surface-2 px-3 py-1.5 text-[10px] font-semibold uppercase leading-none tracking-[0.22em] text-muted md:self-center">
      <span className={`h-1.5 w-1.5 rounded-full ${dotClassName}`} />
      {label}
    </span>
  );
}

export function AccountPage() {
  const requestedPlan = new URLSearchParams(window.location.search).get("plan");
  const requestedPlanName =
    requestedPlan === "pro_yearly" ? "Pro Annually" : requestedPlan === "pro_monthly" ? "Pro Monthly" : null;
  const [session, setSession] = useState<WebSession | null>(() => readSession());
  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [state, setState] = useState<AccountState>("loading");
  const [actionState, setActionState] = useState<ActionState>("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!session) {
      setState("ready");
      return;
    }

    let active = true;
    apiGetAuthed<AccountResponse>("/me", session.accessToken)
      .then((response) => {
        if (!active) return;
        setAccount(response);
        setSession((currentSession) =>
          currentSession
            ? {
                ...currentSession,
                email: response.user.email,
                emailVerified: response.user.emailVerified,
              }
            : currentSession,
        );
        window.localStorage.setItem(
          "ibox-web-session",
          JSON.stringify({
            ...session,
            email: response.user.email,
            emailVerified: response.user.emailVerified,
          }),
        );
        setState("ready");
      })
      .catch((error) => {
        if (!active) return;
        setMessage(error instanceof Error ? error.message : "Your account could not be loaded.");
        setState("error");
      });

    return () => {
      active = false;
    };
  }, [session?.accessToken]);

  async function resendVerification() {
    if (!session) return;
    setActionState("submitting");
    setMessage("");

    try {
      await apiPostAuthed<{ sent: true }>("/auth/resend-verification", {}, session.accessToken);
      setMessage("Verification email sent. Check your inbox for the iBox link.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Verification email could not be sent.");
    } finally {
      setActionState("idle");
    }
  }

  async function startCheckout(plan: "pro_monthly" | "pro_yearly") {
    if (!session) return;
    setActionState("submitting");
    setMessage("");

    try {
      const response = await apiPostAuthed<{ url: string }>("/billing/checkout", { plan }, session.accessToken);
      window.location.href = response.url;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Checkout could not be started.");
      setActionState("idle");
    }
  }

  async function openBillingPortal() {
    if (!session) return;
    setActionState("submitting");
    setMessage("");

    try {
      const response = await apiPostAuthed<{ url: string }>("/billing/portal", {}, session.accessToken);
      window.location.href = response.url;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Billing portal could not be opened.");
      setActionState("idle");
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session) return;

    setActionState("submitting");
    setMessage("");

    const form = new FormData(event.currentTarget);
    const currentPassword = String(form.get("currentPassword") || "");
    const password = String(form.get("password") || "");
    const confirmPassword = String(form.get("confirmPassword") || "");

    if (password !== confirmPassword) {
      setActionState("idle");
      setMessage("Passwords do not match.");
      return;
    }

    try {
      await apiPostAuthed<{ changed: true }>("/auth/change-password", { currentPassword, password }, session.accessToken);
      window.localStorage.removeItem("ibox-web-session");
      setSession(null);
      setAccount(null);
      setMessage("Password updated. Please sign in again with your new password.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Password could not be changed.");
    } finally {
      setActionState("idle");
    }
  }

  async function signOut() {
    const currentSession = session;
    window.localStorage.removeItem("ibox-web-session");
    setSession(null);
    setAccount(null);
    setMessage("");
    setState("ready");

    if (currentSession?.refreshToken) {
      await apiPost("/auth/logout", { refreshToken: currentSession.refreshToken }).catch(() => undefined);
    }
  }

  const isVerified = account?.user.emailVerified ?? session?.emailVerified ?? false;
  const hasPro = account?.entitlement.premiumActive ?? false;
  const billingPlan = account?.billingPlan ?? "rookie";
  const messageIsError =
    state === "error" ||
    message.startsWith("Passwords") ||
    message.startsWith("Your current") ||
    message.includes("could not") ||
    message.includes("not correct");

  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-45" />
        <div className="absolute inset-0 bg-bg/34" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-5xl place-items-center px-6 py-24">
        <Card interactive={false} className="w-full p-0">
          <div className="flex flex-col gap-6 border-b border-border p-6 md:flex-row md:items-start md:justify-between md:p-8">
            <div>
              <StatusPill
                status={!session ? "idle" : isVerified ? "success" : "warning"}
                label={!session ? "SIGNED OUT" : isVerified ? "EMAIL VERIFIED" : "VERIFY EMAIL"}
              />
              <h1 className="mt-5 font-display text-5xl font-bold uppercase leading-none text-text md:text-6xl">iBox Account</h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted">
                Manage your account status, email verification, and iBox Pro access.
              </p>
            </div>
            <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-bg text-primary">
              <ShieldCheck size={26} strokeWidth={1.6} />
            </div>
          </div>

          {!session ? (
            <div className="p-6 md:p-8">
              <p className="text-base leading-relaxed text-muted">
                {requestedPlanName
                  ? `Sign in or create an account to start ${requestedPlanName} checkout after email verification.`
                  : "Sign in or create an account to manage iBox billing and verification."}
              </p>
              {message ? <p className="mt-5 text-sm font-semibold text-success">{message}</p> : null}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <LinkButton href={requestedPlan ? `/sign-in?mode=register&plan=${encodeURIComponent(requestedPlan)}` : "/sign-in"}>
                  Sign In <ArrowRight size={14} />
                </LinkButton>
                <LinkButton href="/download" variant="ghost">
                  Get iBox
                </LinkButton>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              <section className="grid gap-6 p-6 md:grid-cols-[1fr_auto] md:items-center md:p-8">
                <div>
                  <h2 className="font-display text-3xl font-bold uppercase leading-none text-text">{session.email}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted">
                    {isVerified
                      ? "Your email is verified. You can use account billing and future cloud features."
                      : "Verify your email before starting iBox Pro checkout."}
                  </p>
                </div>
                <AccountPlanBadge billingPlan={hasPro ? billingPlan : "rookie"} />
              </section>

              {!isVerified ? (
                <section className="grid gap-6 p-6 md:grid-cols-[1fr_auto] md:items-center md:p-8">
                  <div>
                    <h3 className="font-display text-2xl font-bold uppercase leading-none text-text">Email Verification</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
                      We will send a fresh verification link to your email address. After you click it, return here to start billing.
                    </p>
                  </div>
                  <Button type="button" variant="ghost" disabled={actionState === "submitting"} onClick={resendVerification}>
                    {actionState === "submitting" ? <RefreshCw size={14} /> : <MailCheck size={14} />}
                    Resend Email
                  </Button>
                </section>
              ) : (
                <section className="grid gap-6 p-6 md:grid-cols-[1fr_auto] md:items-center md:p-8">
                  <div>
                    <h3 className="font-display text-2xl font-bold uppercase leading-none text-text">iBox Pro</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
                      {hasPro
                        ? "Manage your iBox Pro subscription, billing details, payment method, and cancellation through Stripe."
                        : requestedPlanName
                        ? `${requestedPlanName} is ready when you are. Start checkout to unlock Pro features.`
                        : "Choose a monthly or yearly plan when you are ready to unlock Pro features."}
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    {hasPro ? (
                      <Button type="button" disabled={actionState === "submitting"} onClick={openBillingPortal}>
                        Manage Billing <ArrowRight size={14} />
                      </Button>
                    ) : (
                      <>
                        <Button type="button" disabled={actionState === "submitting"} onClick={() => startCheckout("pro_monthly")}>
                          Monthly <ArrowRight size={14} />
                        </Button>
                        <Button type="button" variant="ghost" disabled={actionState === "submitting"} onClick={() => startCheckout("pro_yearly")}>
                          Yearly
                        </Button>
                      </>
                    )}
                  </div>
                </section>
              )}

              <section className="p-6 md:p-8">
                <div className="mb-6">
                  <h3 className="font-display text-2xl font-bold uppercase leading-none text-text">Change Password</h3>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
                    Update your iBox account password. After it changes, we will sign you out on this device.
                  </p>
                </div>
                <form className="grid gap-4 md:grid-cols-3" onSubmit={changePassword}>
                  <label className="block space-y-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Current Password</span>
                    <input
                      required
                      type="password"
                      name="currentPassword"
                      autoComplete="current-password"
                      className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                      placeholder="Current password"
                    />
                  </label>
                  <label className="block space-y-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">New Password</span>
                    <input
                      required
                      type="password"
                      name="password"
                      minLength={10}
                      autoComplete="new-password"
                      className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                      placeholder="At least 10 characters"
                    />
                  </label>
                  <label className="block space-y-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Confirm Password</span>
                    <input
                      required
                      type="password"
                      name="confirmPassword"
                      autoComplete="new-password"
                      className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                      placeholder="Repeat password"
                    />
                  </label>
                  <div className="md:col-span-3">
                    <Button type="submit" variant="ghost" disabled={actionState === "submitting"}>
                      {actionState === "submitting" ? "Updating" : "Update Password"}
                    </Button>
                  </div>
                </form>
              </section>

              {message ? <p className={`p-6 text-sm font-semibold md:p-8 ${messageIsError ? "text-error" : "text-success"}`}>{message}</p> : null}

              <section className="flex flex-col gap-3 p-6 md:flex-row md:p-8">
                <Button type="button" variant="ghost" onClick={signOut}>
                  <LogOut size={14} />
                  Sign Out
                </Button>
                <LinkButton href="/download" variant="ghost">
                  Download iBox
                </LinkButton>
              </section>
            </div>
          )}
        </Card>
      </section>
    </main>
  );
}
